import { type FC, useEffect } from 'react';

import {
  Button,
  Flex,
  HStack,
  RangeSlider,
  RangeSliderFilledTrack,
  RangeSliderThumb,
  RangeSliderTrack,
  VStack,
} from '@chakra-ui/react';
import { type SubmitHandler, useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';

import { Form, FormField } from '@/components/Form';

export const RANGE_MIN_VALUE = 0;
export const RANGE_MAX_VALUE = 100;

type FormInputs = {
  min: number;
  max: number;
  range: Array<number>;
};

type RangeFilterComponentProps = {
  rangeInitialValue: Array<number>;
  onSubmit: (newMin: number, newMax: number) => void;
  onclose: () => void;
  customMin?: number;
  customMax?: number;
};

export const RangeFilterComponent: FC<RangeFilterComponentProps> = ({
  rangeInitialValue,
  customMin: CUSTOM_MIN,
  customMax: CUSTOM_MAX,
  onSubmit,
  onclose,
}) => {
  const { t } = useTranslation(['common']);

  const form = useForm<FormInputs>({
    mode: 'onBlur',
    defaultValues: {
      min: rangeInitialValue[0],
      max: rangeInitialValue[1],
      range: [rangeInitialValue[0], rangeInitialValue[1]],
    },
  });

  const { setValue, watch } = form;
  const minValue = watch('min');
  const maxValue = watch('max');

  const onFormSubmit: SubmitHandler<FormInputs> = async (data) => {
    onSubmit(
      data.range[0] ?? CUSTOM_MIN ?? RANGE_MIN_VALUE,
      data.range[1] ?? CUSTOM_MAX ?? RANGE_MAX_VALUE
    );
    onclose();
  };

  useEffect(() => {
    setValue(
      'range',
      // Avoiding user to surpass valid range limit.
      [
        Math.max(minValue, CUSTOM_MIN ?? RANGE_MIN_VALUE),
        Math.min(maxValue, CUSTOM_MAX ?? RANGE_MAX_VALUE),
      ]
    );
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [minValue, maxValue]);

  return (
    <Flex flexDir="column" p={3}>
      <Form {...form} onSubmit={onFormSubmit}>
        <VStack rowGap={4}>
          <FormField
            type="custom"
            control={form.control}
            name="range"
            render={({ field }) => {
              return (
                <RangeSlider
                  {...field}
                  colorScheme="brand"
                  aria-label={['min', 'max']}
                  min={CUSTOM_MIN ?? RANGE_MIN_VALUE}
                  max={CUSTOM_MAX ?? RANGE_MAX_VALUE}
                  onChange={(newValue) => {
                    field.onChange(newValue);
                    setValue('min', newValue[0] as number);
                    setValue('max', newValue[1] as number);
                  }}
                >
                  <RangeSliderTrack height="8px" borderRadius="4px">
                    <RangeSliderFilledTrack />
                  </RangeSliderTrack>
                  <RangeSliderThumb index={0} bgColor="purple.200" />
                  <RangeSliderThumb index={1} bgColor="purple.200" />
                </RangeSlider>
              );
            }}
          />

          <HStack columnGap={3}>
            <FormField
              type="number"
              label={t('common:min') + '.'}
              control={form.control}
              name="min"
            />

            <FormField
              type="number"
              label={t('common:max') + '.'}
              control={form.control}
              name="max"
            />
          </HStack>
        </VStack>

        <Button type="submit" mt={4} variant="@primary" size="sm" w="full">
          {t('common:actions.applyFilter')}
        </Button>
      </Form>
    </Flex>
  );
};

export const getRangeScoreFrom = (
  rangeSearchTerm: Array<number>,
  customMin?: number
): number | undefined => {
  return rangeSearchTerm?.length === 2 &&
    rangeSearchTerm[0] !== (customMin ?? RANGE_MIN_VALUE)
    ? rangeSearchTerm[0]
    : undefined;
};

export const getRangeScoreTo = (
  rangeSearchTerm: Array<number>,
  customMax?: number
): number | undefined => {
  return rangeSearchTerm?.length === 2 &&
    rangeSearchTerm[1] !== (customMax ?? RANGE_MAX_VALUE)
    ? rangeSearchTerm[1]
    : undefined;
};
